import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Lock, LogIn, LogOut, UserPlus, Users as UsersIcon, Shield, Trash2, KeyRound } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "shpnmx_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=authed
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const setAxiosToken = useCallback((tk) => {
    if (tk) axios.defaults.headers.common["Authorization"] = `Bearer ${tk}`;
    else delete axios.defaults.headers.common["Authorization"];
  }, []);

  useEffect(() => {
    setAxiosToken(token);
    if (!token) { setUser(false); return; }
    axios.get(`${API}/auth/me`)
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAxiosToken(""); setUser(false); });
  }, [token, setAxiosToken]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setAxiosToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAxiosToken("");
    setUser(false);
  };

  const value = useMemo(() => ({ user, token, login, logout }), [user, token]); // eslint-disable-line
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4">
      <Toaster richColors position="top-right" />
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-rose-600 text-sm font-bold text-white">R</div>
          <div>
            <div className="text-base font-semibold tracking-tight text-zinc-900">RedClub</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Reporting dashboard</div>
          </div>
        </div>
        <Card className="border-zinc-200/70 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2"><Lock className="h-4 w-4" /> Sign in</CardTitle>
            <CardDescription>Enter your credentials. Accounts are created by an admin.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submit}>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Email</Label>
                <Input
                  type="email" autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Password</Label>
                <Input
                  type="password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" required
                  data-testid="login-password-input"
                />
              </div>
              {error ? (
                <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs text-rose-900" data-testid="login-error">
                  {error}
                </div>
              ) : null}
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                <LogIn className="h-4 w-4" /> {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="mt-6 text-center text-[11px] text-zinc-400">
          No public sign-up. Ask an admin to create your account.
        </p>
      </div>
    </div>
  );
}

export function UserMenu() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-t border-zinc-200">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-semibold text-white">
        {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-zinc-900">{user.name || user.email}</div>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-zinc-500">
          <Shield className="h-3 w-3" />{user.role}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={logout} title="Logout" data-testid="logout-btn" className="h-7 px-2">
        <LogOut className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  // create form
  const [cEmail, setCEmail] = useState("");
  const [cPassword, setCPassword] = useState("");
  const [cName, setCName] = useState("");
  const [cRole, setCRole] = useState("user");
  const [cErr, setCErr] = useState("");

  // password reset
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwErr, setPwErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load users");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCErr("");
    try {
      await axios.post(`${API}/users`, { email: cEmail.trim(), password: cPassword, name: cName.trim() || null, role: cRole });
      toast.success("User created");
      setCEmail(""); setCPassword(""); setCName(""); setCRole("user");
      setOpenCreate(false);
      load();
    } catch (err) {
      setCErr(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    setPwErr("");
    try {
      await axios.patch(`${API}/users/${pwTarget.id}`, { password: pwValue });
      toast.success("Password updated");
      setPwTarget(null); setPwValue("");
    } catch (err) {
      setPwErr(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const changeRole = async (u, newRole) => {
    try {
      await axios.patch(`${API}/users/${u.id}`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-zinc-200 pb-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Users</h2>
            <Badge variant="secondary" className="text-[10px]">Admin</Badge>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-zinc-500">Create accounts for your team. There's no public sign-up.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="users-add-btn"><UserPlus className="h-4 w-4" /> Add user</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create user</DialogTitle>
              <DialogDescription>Set their email and an initial password — they can sign in immediately.</DialogDescription>
            </DialogHeader>
            <form className="space-y-3" onSubmit={create}>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Email</Label>
                <Input type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} required data-testid="users-create-email" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Name (optional)</Label>
                <Input value={cName} onChange={(e) => setCName(e.target.value)} data-testid="users-create-name" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Initial password</Label>
                <Input type="text" value={cPassword} onChange={(e) => setCPassword(e.target.value)} required minLength={6} data-testid="users-create-password" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Role</Label>
                <Select value={cRole} onValueChange={setCRole}>
                  <SelectTrigger data-testid="users-create-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cErr ? <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs text-rose-900">{cErr}</div> : null}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpenCreate(false)}>Cancel</Button>
                <Button type="submit" data-testid="users-create-submit">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-zinc-200/70">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><UsersIcon className="h-4 w-4" /> {users.length} {users.length === 1 ? "user" : "users"}</CardTitle>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border border-zinc-200" data-testid="users-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-[11px] uppercase tracking-wide text-zinc-500">Email</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-zinc-500">Name</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-zinc-500">Role</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-zinc-500">Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="hover:bg-zinc-50/60">
                    <TableCell className="font-mono text-sm">{u.email}{me?.id === u.id ? <span className="ml-2 text-[10px] text-zinc-400">(you)</span> : null}</TableCell>
                    <TableCell className="text-sm">{u.name || <span className="text-zinc-400">—</span>}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(r) => changeRole(u, r)} disabled={me?.id === u.id}>
                        <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">user</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-zinc-500">{u.created_at ? new Date(u.created_at).toISOString().slice(0, 10) : "—"}</TableCell>
                    <TableCell className="space-x-1 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setPwTarget(u); setPwValue(""); setPwErr(""); }} title="Change password" data-testid={`users-pwd-${u.email}`}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(u)} disabled={me?.id === u.id} title="Delete" data-testid={`users-del-${u.email}`} className="text-rose-600 hover:text-rose-700">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!pwTarget} onOpenChange={(v) => !v && setPwTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>{pwTarget?.email}</DialogDescription>
          </DialogHeader>
          <form className="space-y-3" onSubmit={submitPw}>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">New password</Label>
              <Input type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)} required minLength={6} data-testid="users-pw-input" />
            </div>
            {pwErr ? <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs text-rose-900">{pwErr}</div> : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPwTarget(null)}>Cancel</Button>
              <Button type="submit" data-testid="users-pw-submit">Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
